import React, { Component, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ToastProvider } from './components/Toast';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import RootRedirect from './components/RootRedirect';
import AppLoader from './components/AppLoader';
import './styles/tokens.css';
import './styles/typography.css';
import './styles/layout.css';
import './styles/components.css';
import './styles/animations.css';
import './styles/ambient.css';
import './App.css';

const LoginScreen = lazy(() => import('./pages/LoginScreen'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AppShell = lazy(() => import('./layouts/AppShell'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Calculator = lazy(() => import('./pages/Calculator/Calculator'));
const History = lazy(() => import('./pages/History/History'));
const Analytics = lazy(() => import('./pages/Analytics/Analytics'));
const Achievements = lazy(() => import('./pages/Achievements/Achievements'));
const ImportPage = lazy(() => import('./pages/Import/Import'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const About = lazy(() => import('./pages/About/About'));

const CHUNK_RELOAD_KEY = 'chunk-reload-at';
// Reload at most once per minute: a stale-chunk error recurring right after
// a reload means the deploy itself is broken — fall through to the error
// screen instead of looping.
const CHUNK_RELOAD_COOLDOWN_MS = 60000;

// After a deploy, tabs opened before it still reference old hashed chunk
// filenames that no longer exist — lazy() route loads then throw
// ChunkLoadError. A reload fetches the new index.html and fixes it.
function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const message = error.message || '';
  return (
    name === 'ChunkLoadError' ||
    /chunkloaderror/i.test(name) ||
    /loading.*chunk.*failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

function shouldAutoReload(error) {
  if (!isChunkLoadError(error)) return false;
  try {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    return Date.now() - lastReload > CHUNK_RELOAD_COOLDOWN_MS;
  } catch {
    // No sessionStorage → no loop guard → don't auto-reload.
    return false;
  }
}

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isReloading: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, isReloading: shouldAutoReload(error) };
  }
  componentDidMount() {
    if (!this.state.hasError) {
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch {}
    }
  }
  componentDidCatch() {
    if (this.state.isReloading) {
      try {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      } catch {}
      window.location.reload();
    }
  }
  render() {
    if (this.state.isReloading) {
      return <AppLoader />;
    }
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', background: 'var(--app-bg)', color: 'var(--text-primary)', padding: 24, textAlign: 'center',
          fontFamily: "'Inter', sans-serif", gap: 12,
        }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Something went wrong</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Please try refreshing the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 12, padding: '10px 24px', borderRadius: 8, border: 'none',
              background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function NotFound() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: 'var(--app-bg)', color: 'var(--text-primary)', padding: 24, textAlign: 'center',
      fontFamily: "'Inter', sans-serif", gap: 12,
    }}>
      <h1 style={{ fontSize: 48, fontWeight: 800, margin: 0, color: 'var(--accent)' }}>404</h1>
      <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Page not found</p>
      <p style={{ color: 'var(--text-secondary)', margin: 0 }}>The page you're looking for doesn't exist.</p>
      <a href="/" style={{
        marginTop: 12, padding: '10px 24px', borderRadius: 8, border: 'none',
        background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
        textDecoration: 'none',
      }}>
        Go Home
      </a>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Suspense fallback={<AppLoader />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />

            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginScreen />
                </PublicRoute>
              }
            />

            {/* Reachable signed in or out: the emailed link must always work. */}
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/calculator" element={<Calculator />} />
              <Route path="/history" element={<History />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/achievements" element={<Achievements />} />
              <Route path="/import" element={<ImportPage />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/about" element={<About />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
